import { useEffect, useState } from 'react';
import {
  listFavoriteLines, saveFavoriteLine, deleteFavoriteLine, type FavoriteLineDto,
} from '../../api/approval';
import {
  getLineSuggestion, LINE_SLOT_LABEL, type LineSuggestionDto,
} from '../../api/hr-org-leader';
import { ApproverPicker, type PickedUser } from './ApproverPicker';
// 공용 즐겨찾기 (2026-08-28) — 만들 수 있는 사람에게만 그 칸을 보인다(판정은 서버가 다시 한다).
import { useIsAdminViewer } from '../../lib/menu-catalog';
import { Button, Checkbox, Input, Select } from '../../components/shared';
// 대시보드 시안 v2 V9 (2026-09-15) — 즐겨찾는 결재선 고르개 · 되돌리기 · 저장 칸. `favoritesVariant="select"` 일 때만.
import { FavoriteLineSelect, FavoriteLineAfter } from './FavoriteLineSection';
import { favoriteToLine } from './favorite-line-view';

/**
 * 결재선 블록 — **공통 조각 하나** (결재 A6, 2026-08-24).
 *
 * 설계: *"★★결재선 블록은 공통 조각 하나다 — 휴가·외근·출장·정정·연장·지출결의서가 같이 쓴다.
 * **위 칸(서식)만 종류마다 다르고 아래는 똑같다.**"*
 *
 * ★★ 이것을 나누지 않으면 근태에서 신청할 때와 결재에서 기안할 때 **사람 고르는 방법이 달라진다.**
 *    실제로 그랬다 — 근태 신청은 결재선을 아예 못 보고 서버가 자동으로 뽑은 것을 받기만 했다.
 * ★ 자동 도출·전결·기안자 수정 허용 판정은 **서버가 한다**(`/approval/line-suggestion`).
 *   여기서는 그 결과를 보여 주고 고칠 뿐이다 — 규칙을 화면에 복제하면 서버와 갈라진다.
 * ★ 손으로 이미 고른 사람은 덮지 않는다. 화면이 고른 것을 지워 버리면 안 된다.
 */

export interface ApprovalLineValue {
  approvers: PickedUser[];
  referrers: PickedUser[];
}

export const EMPTY_LINE: ApprovalLineValue = { approvers: [], referrers: [] };

interface Props {
  /** 문서 종류 코드(`LEAVE` 등) — 종류마다 기본 결재선·전결 기준이 다르다. */
  docType: string;
  /** 이 문서의 크기(일수·금액) — 전결 판정에 쓴다. 없으면 전결하지 않는다. */
  measure?: number | null;
  value: ApprovalLineValue;
  onChange: (next: ApprovalLineValue) => void;
  disabled?: boolean;
  /** 저장 실패 등을 부르는 쪽 알림 자리에 띄운다. */
  onError?: (message: string) => void;
  /** 부르는 쪽이 서식(금액 칸 등)을 결정할 수 있게 서버 판정을 그대로 넘긴다. */
  onSuggestion?: (s: LineSuggestionDto | null) => void;
  /**
   * 즐겨찾는 결재선 모양 (2026-09-15 · 대시보드 시안 v2 V9).
   * `inline`(기본) = 종전 「자주 쓰는 결재선」 줄 그대로 ·
   * `select` = 결재자 **위** 고르개(회사 공용 / 내 결재선) + 「되돌리기」 + 저장 칸.
   * ★ 결재 기안 · 대체휴가 화면은 이 칸을 안 주므로 종전 그대로다.
   */
  favoritesVariant?: 'inline' | 'select';
}

/**
 * 앞 결재자는 최대 셋 (정본 B11-3) — 서버 `checkLineSize` 와 같은 값이다.
 * ⚠ 이름이 `MAX_REVIEWERS` 인 것은 «옛 이름(검토자)» 자취다 — 2026-09-24 에 라벨만 바뀌었고
 *   수·규칙은 그대로라 이름은 건드리지 않았다. 「최대 검토자 수」로 읽지 말 것.
 */
const MAX_REVIEWERS = 3;

export function ApprovalLineBlock({
  docType, measure = null, value, onChange, disabled = false, onError, onSuggestion,
  favoritesVariant = 'inline',
}: Props) {
  const [suggestion, setSuggestion] = useState<LineSuggestionDto | null>(null);
  const [favorites, setFavorites] = useState<FavoriteLineDto[]>([]);
  const [favName, setFavName] = useState('');
  /** 지우는 중인 즐겨찾기 — 두 번 눌러도 두 번 지우지 않게. */
  const [removing, setRemoving] = useState<string | null>(null);
  /**
   * 공용으로 저장할까 (2026-08-28).
   *
   * ★ 관리자에게만 이 칸을 보인다 — 못 켜는 사람에게 보이면 눌렀다 403 을 받는다.
   *   ⚠ 화면이 가리는 것은 **친절**이고, 막는 것은 서버가 한다.
   */
  const canShare = useIsAdminViewer();
  const [saveShared, setSaveShared] = useState(false);

  /* ── v2 V9 (`favoritesVariant="select"`) ─────────────────────────────────── */
  const select = favoritesVariant === 'select';
  /** 지금 결재선이 어느 즐겨찾기에서 왔나(고르개 트리거). 손으로 고치면 비운다. */
  const [sourceId, setSourceId] = useState<string | null>(null);
  /** 즐겨찾기로 채우기 직전 값 — 「되돌리기」. 손으로 고치면 사라진다(규칙 D6). */
  const [undo, setUndo] = useState<{ name: string; prev: ApprovalLineValue } | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  /** 즐겨찾기 목록을 한 번이라도 다 읽었나(성공이든 실패든). */
  const [favLoaded, setFavLoaded] = useState(false);

  // ★ `agreers`(합의)는 **읽지 않는다** — 새 문서에서 세우지 않는 칸이다(판 P1 · 결정 ①).
  //   값은 타입에 남는다: 부르는 쪽이 옛 문서를 그대로 넘길 수 있어야 한다.
  const { approvers, referrers } = value;

  /**
   * ★★★ 결재 줄 하나를 **결재자 여럿 + 최종결재자 하나**로 나눠 그린다 (판 P1).
   *    저장은 `approvers` 한 배열이고 **마지막 하나가 「최종결재자」**다 — 서버도 같은 규칙으로 읽는다
   *    (`step === totalSteps`). 화면과 서버가 같은 말을 쓰지 않으면 자리가 어긋난다.
   */
  const reviewers = approvers.slice(0, -1);
  const finalApprover = approvers.slice(-1);

  /**
   * 사람 고르개가 부르는 onChange — `select` 모양에서는 «손으로 고친 것»이라 출처 · 되돌리기를 비운다.
   * ★ `inline`(기본)이면 `onChange` 를 그대로 부르는 것과 같다.
   */
  const change = (next: ApprovalLineValue) => {
    if (select) { setSourceId(null); setUndo(null); }
    onChange(next);
  };

  const pickFavorite = (fav: FavoriteLineDto) => {
    setUndo({ name: fav.name, prev: value });
    setSourceId(fav.id);
    onChange(favoriteToLine(fav, value));
  };

  const undoFavorite = () => {
    if (!undo) return;
    onChange(undo.prev);
    setSourceId(null);
    setUndo(null);
  };

  const removeFavorite = (fav: FavoriteLineDto) => {
    // 되돌릴 수 없다 — 묻고 지운다(종전 「지우기」 줄과 같은 말).
    if (!window.confirm(
      fav.shared
        ? `«${fav.name}» 은 회사 공용입니다 — 모두의 목록에서 사라집니다. 지울까요?`
        : `«${fav.name}» 결재선을 지울까요?`,
    )) return;
    setRemoving(fav.id);
    void deleteFavoriteLine(fav.id)
      .then(() => listFavoriteLines())
      .then((list) => {
        setFavorites(list);
        if (sourceId === fav.id) setSourceId(null);
      })
      .catch(() => onError?.('결재선을 지우지 못했습니다.'))
      .finally(() => setRemoving(null));
  };

  const saveFavorite = async ({ name, shared }: { name: string; shared: boolean }): Promise<boolean> => {
    try {
      const res = await saveFavoriteLine({
        name,
        approverIds: approvers.map((a) => a.userId),
        referrerIds: referrers.map((r) => r.userId),
        // ★ 켤 때만 싣는다 — 옛 서버는 이 칸을 모르고 400 을 낸다.
        ...(shared ? { shared: true } : {}),
      });
      const list = await listFavoriteLines();
      setFavorites(list);
      setSourceId(res?.id ?? null);
      setUndo(null);
      setSaveOpen(false);
      return true;
    } catch {
      onError?.('결재선을 저장하지 못했습니다.');
      return false;
    }
  };

  // 즐겨찾는 결재선 — 종류에 묶지 않는다("이름은 자유이고 어느 문서에서든 불러온다", 보완 ⑪).
  useEffect(() => {
    listFavoriteLines().then(setFavorites).catch(() => { /* 옛 서버·오류 — 즐겨찾기만 없다 */ })
      // v2 V9 — 다 읽기 전에는 「없음」이라고 말하지 않는다(모른다 ≠ 없다).
      .finally(() => setFavLoaded(true));
  }, []);

  // 종류·크기가 바뀌면 그 조건의 사다리를 다시 뽑는다(휴가 2단계 · 전결이면 1단계 …).
  useEffect(() => {
    let alive = true;
    getLineSuggestion(docType, measure)
      .then((s) => {
        if (!alive) return;
        setSuggestion(s);
        onSuggestion?.(s);
        // ★ 손으로 이미 고른 사람은 덮지 않는다.
        if (approvers.length === 0 && !s.blocked) {
          onChange({
            ...value,
            approvers: s.lines
              .filter((l) => l.lineType === 'APPROVE')
              .map((l) => ({ userId: l.approverId, name: l.name ?? l.approverId, position: l.position ?? null })),
          });
          // v2 V9 — 기본 결재선으로 채웠으면 고르개에 그 이름을 세운다(`select` 모양에서만 보인다).
          if (s.defaultLine) setSourceId(s.defaultLine.id);
        }
      })
      .catch(() => {
        if (!alive) return;
        setSuggestion(null);      // 옛 서버(404) — 직접 고른다.
        onSuggestion?.(null);
      });
    return () => { alive = false; };
    // value/onChange 를 넣으면 고를 때마다 다시 물어본다 — 종류와 크기가 바뀔 때만 뽑는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, measure]);

  /** 이 종류는 기안자가 결재선을 고칠 수 있는가 (결재 A1). 서버도 같은 판정으로 막는다. */
  const lineEditable = suggestion?.allowDrafterEdit ?? true;

  return (
    <>
      {/* 전결이 걸리면 **그 사실과 이유**를 미리 말한다 — 결재선이 짧아진 것이
          빠뜨린 것으로 보이면 안 된다 (결재 A3). */}
      {suggestion?.finalized && (
        <p className="approval-compose__suggestion">
          <b>전결</b>입니다 — {suggestion.finalize?.threshold}
          {suggestion.finalize?.kind === 'DAYS' ? '일' : '원'} 이하라 <b>윗단계를 뺐습니다</b>.
          회사가 정한 기준이며, 문서 종류에서 바꿀 수 있습니다.
        </p>
      )}

      {/* ★ 회사가 "기안자는 결재선을 못 고친다" 고 정한 종류면 고르는 칸을 아예 열지 않는다
          (결재 A1). 서버도 같은 판정으로 보내온 결재선을 버리고 **회사 기본 결재선**으로
          덮어쓰므로, 열어 두면 고를 수는 있는데 반영되지 않는 칸이 된다 — 그것이 제일 나쁘다. */}
      {!lineEditable && (
        <p className="approval-compose__suggestion">
          이 종류는 <b>회사가 정한 기본 결재선</b>으로만 올라갑니다 — 위에 보이는 그대로 진행됩니다.
        </p>
      )}

      {/*
        ★★★ **결재자와 최종결재자는 «같은 줄의 자리 차이»다** (2026-09-04 판 P1 · 이름 2026-09-24).
             둘 다 결재 줄(`APPROVE`)이고 **마지막 한 사람이 「최종결재자」**, 그 앞이 「결재자」다.
             그래서 칸은 둘이지만 저장은 `approvers` **한 배열**이다 — 화면이 자리를 나누고
             서버는 순서만 본다(마이그레이션 0 · 판정 코드 무변경).
        ★★ 「합의」 칸은 **세우지 않는다**(결정 ①). 진행을 막지 않는 줄을 결재선에 그리면
             「저 사람이 안 눌러서 멈춰 있다」로 잘못 읽힌다. 옛 문서의 합의 줄은 상세에서
             표시만 하고, 여기서는 새로 만들 수 없다.
      */}
      {/* v2 V9 — 즐겨찾는 결재선 고르개는 **결재자 위**다(시안). 못 고치는 종류면 세우지 않는다 —
          골라 봐야 서버가 회사 기본 결재선으로 덮어쓴다. */}
      {select && lineEditable && (
        <FavoriteLineSelect
          docType={docType}
          favorites={favorites}
          sourceId={sourceId}
          loading={!favLoaded}
          canManage={canShare}
          disabled={disabled || removing !== null}
          onPick={pickFavorite}
          onRemove={removeFavorite}
          onOpenSave={() => setSaveOpen(true)}
        />
      )}

      {lineEditable && (
        <>
          <ApproverPicker
            label="결재자"
            hint={`거쳐 가는 사람 · 순서대로 올라갑니다 · 없어도 됩니다 (최대 ${MAX_REVIEWERS}명)`}
            value={reviewers}
            max={MAX_REVIEWERS}
            onChange={(v) => change({ ...value, approvers: [...v, ...finalApprover] })}
            exclude={[...finalApprover.map((a) => a.userId), ...referrers.map((r) => r.userId)]}
          />

          <ApproverPicker
            label="최종결재자"
            hint="마지막 한 사람 · 비울 수 없습니다"
            value={finalApprover}
            max={1}
            onChange={(v) => change({ ...value, approvers: [...reviewers, ...v.slice(-1)] })}
            exclude={[...reviewers.map((a) => a.userId), ...referrers.map((r) => r.userId)]}
          />
        </>
      )}

      {/* 참고자 — 결재를 하지 않는다. 하위에 명단으로만 보인다(사용자 결정 2026-09-04). */}
      <ApproverPicker
        label="참고자"
        hint="결재하지 않습니다 · 문서를 볼 수만 있습니다"
        value={referrers}
        onChange={(v) => change({ ...value, referrers: v })}
        exclude={[...approvers.map((a) => a.userId)]}
      />

      {/* v2 V9 — 되돌리기 · 저장 칸 · 둘 다 없을 때 안내. */}
      {select && (
        <FavoriteLineAfter
          value={value}
          favorites={favorites}
          undoName={undo?.name ?? null}
          saveOpen={saveOpen}
          canShare={canShare}
          disabled={disabled}
          showEmptyNote={favLoaded && favorites.length === 0 && !suggestion?.defaultLine && approvers.length === 0}
          onUndo={undoFavorite}
          onOpenSave={() => setSaveOpen(true)}
          onCloseSave={() => setSaveOpen(false)}
          onSave={saveFavorite}
        />
      )}

      {/* 즐겨찾는 결재선 — 종류에 묶지 않는다. 이름은 자유이고 어느 문서에서든 부른다. */}
      {!select && (
      <div className="approval-compose__field">
        <span>자주 쓰는 결재선</span>
        <div className="approval-compose__favs">
          {/*
            ★ **고르는 칸이 아니라 «부르는» 칸이다** — 고른 값을 담아 두지 않는다(`value=null`).
              한 번 부른 결재선을 다시 부를 수 있어야 하기 때문이다.
          */}
          <Select
            ariaLabel="자주 쓰는 결재선 불러오기"
            className="approval-compose__favload"
            value={null}
            placeholder="불러오기…"
            disabled={disabled}
            options={favorites.map((f) => ({
              value: f.id,
              // ★ 공용은 이름 앞에 밝힌다 — 내 것과 회사 것이 한 목록에 섞여 있다.
              label: `${f.shared ? '[공용] ' : ''}${f.name}${f.missing > 0 ? ` (빠진 사람 ${f.missing})` : ''}`,
            }))}
            onChange={(picked) => {
              const fav = favorites.find((f) => f.id === picked);
              if (!fav) return;
              // 이름을 모르는 사람(회사를 떠난)은 빼고 담는다 — 담아 봐야 기안이 막힌다.
              onChange({
                ...value,
                approvers: fav.approvers.filter((a) => a.name)
                  .map((a) => ({ userId: a.userId, name: a.name as string, position: null })),
                referrers: fav.referrers.filter((r) => r.name)
                  .map((r) => ({ userId: r.userId, name: r.name as string, position: null })),
              });
            }}
          />
          <Input
            wrapperClassName="approval-compose__favname"
            type="text"
            value={favName}
            onChange={(e) => setFavName(e.target.value)}
            placeholder="이름 붙여 저장"
            maxLength={100}
            aria-label="결재선 이름"
          />
          {/* ★ 공용은 회사 관리자만 만든다 — 다른 사람에게는 이 칸이 아예 없다. */}
          {canShare && (
            <Checkbox
              label="회사 공용으로"
              checked={saveShared}
              disabled={disabled}
              onChange={(e) => setSaveShared(e.target.checked)}
            />
          )}
          <Button
            variant="soft"
            size="sm"
            disabled={disabled || approvers.length === 0 || favName.trim() === ''}
            onClick={() => {
              void saveFavoriteLine({
                name: favName.trim(),
                approverIds: approvers.map((a) => a.userId),
                referrerIds: referrers.map((r) => r.userId),
                // ★ **켤 때만 싣는다** — 옛 서버는 이 칸을 모르고 400 을 낸다.
                ...(saveShared ? { shared: true } : {}),
              })
                .then(() => listFavoriteLines())
                .then((list) => { setFavorites(list); setFavName(''); setSaveShared(false); })
                .catch(() => onError?.('결재선을 저장하지 못했습니다.'));
            }}
          >
            지금 결재선 저장
          </Button>
        </div>

        {/*
          ★★ **지우는 길이 없었다** (2026-08-28 · 자리 재정의 판 6).
             `deleteFavoriteLine` 은 API 에 **정의만 있고 부르는 곳이 0** 이었다 —
             한 번 저장한 결재선은 영영 목록에 남아, 사람이 바뀔 때마다 쓸모없는 줄이 쌓였다.

          ★ 불러오는 길(위 셀렉트)은 **건드리지 않는다**. 지우는 것은 다른 일이라 줄을 따로 둔다 —
            셀렉트 안에 «지우기» 를 섞으면 불러오려다 지우는 일이 생긴다.
        */}
        {/*
          ★★ **지울 수 있는 것만 그린다** (2026-08-28) — 판정은 **서버가 준다**(`canDelete`).
             화면이 규칙을 다시 쓰면(「공용이면 관리자」) 서버와 갈라지는 날이 온다.
             옛 서버는 그 칸을 안 줘 종전대로 전부 보인다.
        */}
        {favorites.some((f) => f.canDelete !== false) && (
          <div className="approval-compose__favdel">
            <span className="approval-compose__favdel-label">지우기</span>
            {favorites.filter((f) => f.canDelete !== false).map((f) => (
              <Button
                key={f.id}
                variant="quiet-danger"
                size="sm"
                disabled={disabled || removing === f.id}
                aria-label={`${f.name} 지우기`}
                onClick={() => {
                  // 되돌릴 수 없다 — 묻고 지운다.
                  // 공용은 **모두가 쓰는 것**이라 그 사실을 묻는 말에 적는다.
                  if (!window.confirm(
                    f.shared
                      ? `«${f.name}» 은 회사 공용입니다 — 모두의 목록에서 사라집니다. 지울까요?`
                      : `«${f.name}» 결재선을 지울까요?`,
                  )) return;
                  setRemoving(f.id);
                  void deleteFavoriteLine(f.id)
                    .then(() => listFavoriteLines())
                    .then(setFavorites)
                    .catch(() => onError?.('결재선을 지우지 못했습니다.'))
                    .finally(() => setRemoving(null));
                }}
              >
                {f.shared ? '[공용] ' : ''}{f.name} <span aria-hidden>×</span>
              </Button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* 조직에서 뽑아 온 결재선 — 무엇이 왜 그렇게 됐는지 함께 보여 준다(제안 3). */}
      {suggestion && (
        <p className="approval-compose__suggestion">
          {suggestion.blocked ? (
            <>이 양식은 회사가 정한 결재선으로만 올릴 수 있는데 그 결재선이 등록돼 있지 않습니다 — 관리자에게 알려 주세요.</>
          ) : suggestion.lines.length === 0 ? (
            <>이 양식에는 회사 기본 결재선이 없습니다 — 결재자 · 최종결재자를 직접 고르세요.</>
          ) : (
            <>
              {suggestion.defaultLine
                ? `회사 기본 결재선 「${suggestion.defaultLine.name}」: `
                : '결재선: '}
              {suggestion.lines
                .filter((l) => l.lineType === 'APPROVE')
                .map((l, i, arr) => {
                  // 마지막이 「최종결재자」, 그 앞이 「결재자」 — 옛 서버는 slot 을 안 줘서 자리로 셈한다.
                  const slot = l.slot ?? (i === arr.length - 1 ? 'APPROVE' : 'REVIEW');
                  return `${l.name ?? l.approverId}(${LINE_SLOT_LABEL[slot]})`;
                })
                .join(' → ')}
              {/*
                ★★ **건너뛴 칸을 하나도 빠뜨리지 않는다** (2026-08-28 · 자리 재정의 판 6).
                   서버는 「부서장이 없어 건너뜁니다」처럼 **이유를 이미 다 적어 보내는데**,
                   화면이 `notes[0]` **한 줄만** 그렸다. 부서장도 비고 팀장도 비면 둘째 줄부터는
                   아무도 못 봤다 — 결재선이 왜 짧아졌는지 모른 채 상신하게 된다.
              */}
              {/*
                ⚠ 바깥이 `<p>` 다 — `<ul>` 을 넣으면 **파서가 문단을 먼저 닫아** 그 뒤가
                  통째로 밖으로 밀려난다(경고가 아니라 실제로 깨진다). `<span>` 으로 줄을 만든다.
              */}
              {suggestion.notes.length > 0 && (
                <span className="approval-compose__notes">
                  {suggestion.notes.map((n) => <span key={n}>{n}</span>)}
                </span>
              )}
            </>
          )}
        </p>
      )}
    </>
  );
}
